import React from "react";
import LegalLayout from "@/pages/LegalLayout";

const P = ({ children }) => <p>{children}</p>;
const List = ({ items }) => (
  <ul className="space-y-2 pl-0">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="w-1.5 h-1.5 bg-accent block mt-2.5 shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function PrivacyPolicy() {
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      body: (
        <>
          <P>
            PHOS Management Inc. ("PHOS Management," "we," "us," or "our")
            respects your privacy and is committed to protecting the personal
            information you provide to us.
          </P>
          <P>
            This Privacy Policy explains how we collect, use, and protect
            information when you visit our website, submit a form, contact us,
            request information, schedule a consultation, purchase services, or
            communicate with us by email, phone, SMS, or other methods.
          </P>
        </>
      ),
    },
    {
      id: "information-we-collect",
      title: "Information We Collect",
      body: (
        <>
          <P>We may collect information that you voluntarily provide to us, including:</P>
          <List
            items={[
              "Name",
              "Email address",
              "Phone number",
              "Business name",
              "Mailing or business address",
              "Information submitted through contact forms",
              "Appointment or consultation information",
              "Information related to services you request from us",
              "Communications you send to PHOS Management",
            ]}
          />
          <P>
            We may also automatically collect certain technical information when
            you use our website, such as browser type, device information, IP
            address, referring pages, pages visited, and website usage
            information.
          </P>
        </>
      ),
    },
    {
      id: "how-we-use",
      title: "How We Use Your Information",
      body: (
        <>
          <P>We may use the information you provide to:</P>
          <List
            items={[
              "Respond to inquiries and requests",
              "Provide our products or services",
              "Schedule appointments or consultations",
              "Communicate regarding projects, services, appointments, or accounts",
              "Send requested information",
              "Provide customer support",
              "Send marketing or promotional communications when you have provided appropriate consent",
              "Improve our website, services, advertising, and customer experience",
              "Maintain the security and functionality of our systems",
              "Comply with applicable legal and regulatory requirements",
            ]}
          />
        </>
      ),
    },
    {
      id: "sms-messaging",
      title: "SMS and Text Messaging",
      body: (
        <>
          <P>
            If you provide your mobile phone number and expressly opt in to
            receive SMS communications from PHOS Management Inc., we may send
            you text messages relating to our services, promotions, special
            offers, updates, appointments, inquiries, or other communications
            you have consented to receive.
          </P>
          <List
            items={[
              "Message frequency may vary.",
              "Message and data rates may apply.",
              "You may opt out of SMS communications at any time by replying STOP to a message from us.",
              "You may reply HELP for additional assistance or contact us directly using the information listed below.",
              "Consent to receive marketing text messages is not a condition of purchasing any goods or services.",
            ]}
          />
        </>
      ),
    },
    {
      id: "mobile-consent",
      title: "Mobile Information and SMS Consent",
      body: (
        <>
          <div className="border-l-2 border-accent pl-5 py-2 bg-white/[0.02]">
            <P>
              <strong className="text-ink-50">
                No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
              </strong>{" "}
              Information sharing to subcontractors in support services, such as
              customer service, is permitted. All other use case categories
              exclude text messaging originator opt-in data and consent; this
              information will not be shared with any third parties.
            </P>
          </div>
        </>
      ),
    },
    {
      id: "service-providers",
      title: "Service Providers",
      body: (
        <>
          <P>
            We may use service providers that assist us in operating our
            website, providing customer support, processing communications,
            scheduling services, maintaining our systems, or performing other
            functions necessary to operate our business.
          </P>
          <P>
            These providers may access information only as necessary to perform
            services on our behalf and are expected to protect that information
            appropriately.
          </P>
          <P>
            SMS opt-in information and consent are handled in accordance with
            the Mobile Information and SMS Consent section above.
          </P>
        </>
      ),
    },
    {
      id: "cookies",
      title: "Cookies and Website Technologies",
      body: (
        <>
          <P>
            Our website may use cookies, pixels, analytics technologies, and
            similar tools to understand website usage, improve site
            functionality, measure advertising performance, and improve our
            services.
          </P>
          <P>You may be able to control certain cookies through your browser settings.</P>
        </>
      ),
    },
    {
      id: "data-security",
      title: "Data Security",
      body: (
        <>
          <P>
            We use reasonable administrative, technical, and organizational
            measures designed to protect personal information from unauthorized
            access, disclosure, alteration, misuse, or destruction.
          </P>
          <P>
            However, no method of electronic transmission or storage is
            completely secure, and we cannot guarantee absolute security.
          </P>
        </>
      ),
    },
    {
      id: "data-retention",
      title: "Data Retention",
      body: (
        <P>
          We may retain personal information for as long as reasonably necessary
          to fulfill the purposes described in this Privacy Policy, provide our
          services, maintain business records, resolve disputes, enforce
          agreements, and comply with applicable law.
        </P>
      ),
    },
    {
      id: "your-choices",
      title: "Your Choices",
      body: (
        <>
          <List
            items={[
              "You may request that we update or correct certain personal information by contacting us.",
              "You may unsubscribe from marketing emails using the unsubscribe link contained in those emails.",
              "You may unsubscribe from SMS communications at any time by replying STOP.",
            ]}
          />
        </>
      ),
    },
    {
      id: "childrens-privacy",
      title: "Children's Privacy",
      body: (
        <P>
          Our services are not directed toward children under 13, and we do not
          knowingly collect personal information from children under 13.
        </P>
      ),
    },
    {
      id: "changes",
      title: "Changes to This Privacy Policy",
      body: (
        <>
          <P>We may update this Privacy Policy periodically.</P>
          <P>
            When we make changes, we may update the "Last Updated" date at the
            top of this page. Your continued use of our website or services
            after an updated policy becomes effective constitutes acknowledgment
            of the updated policy.
          </P>
        </>
      ),
    },
  ];

  return (
    <LegalLayout
      testid="privacy-policy-page"
      title={["Privacy", "Policy."]}
      updated="September 21, 2026"
      sections={sections}
      otherPage={{ to: "/terms-of-service", label: "Terms of Service" }}
    />
  );
}
